import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { House, Suitcase, ChatCircleDots, User, CalendarCheck, Receipt, Wallet } from "@phosphor-icons/react";
import { useSettings } from "@/context/SettingsContext";
import { pick } from "@/lib/languages";
import { appShellL } from "@/i18n/appShell";

// The traveller app's four tabs are real routes; /messages is the app's own
// list of conversations (owner, 2026-10-24: the approved Android design).
// Accueil holds every screen of a search (results, a stay, a flight): the
// approved design keeps it lit there; the other tabs hold their own pages.
const TRAVELLER = [
  { to: "/", icon: House, key: "home" },
  { to: "/bookings", icon: Suitcase, key: "trips" },
  { to: "/messages", icon: ChatCircleDots, key: "messages", unread: true },
  { to: "/account", icon: User, key: "profile" },
];
// Toukky Partner's five reuse the dashboard's ?tab= panels; "calendar" lists
// the listings that have a calendar. Home is the overview.
const PARTNER = [
  { tab: "overview", icon: House, key: "home" },
  { tab: "calendar", icon: CalendarCheck, key: "calendar" },
  { tab: "reservations", icon: Receipt, key: "bookings" },
  { tab: "finance", icon: Wallet, key: "payouts" },
  { tab: "messages", icon: ChatCircleDots, key: "messages", unread: true },
];

// Screens where the bar slides away: a sticky action bar (book, pay) or a
// full-screen conversation takes the bottom of the screen.
const HIDDEN = [
  /^\/hotels\/[^/]+$/, /^\/flights\/[^/]+/, /^\/cars\/[^/]+$/, /^\/booking\//,
  /^\/groups\/(?!new\/?$)[^/]+/, /^\/messages\/[^/]+/, /^\/partner\/(property|vehicle|attraction|taxi)\//,
];

export const tabBarHidden = (pathname) => HIDDEN.some((re) => re.test(pathname));

function TabContent({ active, icon: Icon, label, count, countLabel }) {
  return (
    <>
      <span className="tk-tab-pill"><Icon size={24} weight={active ? "fill" : "regular"} aria-hidden="true" /></span>
      {label}
      {count > 0 ? <span className="tk-tab-count" aria-label={countLabel} data-testid="app-tab-unread">{count > 9 ? "9+" : count}</span> : null}
    </>
  );
}

/**
 * The bottom tab bar of the two apps (never the website, which keeps its
 * header). `partner` picks Toukky Partner's five tabs; `unread` is the count
 * the Messages tab carries in red.
 */
export default function AppTabBar({ partner = false, unread = 0 }) {
  const { pathname, search } = useLocation();
  const { lang } = useSettings();
  const l = pick(appShellL, lang).tabs;
  const hidden = tabBarHidden(pathname);
  // While the bar shows, what the site fixes to the bottom of the screen (the
  // cookie banner, the offline band, the toasts) rides above it (index.css).
  useEffect(() => {
    document.documentElement.classList.toggle("tk-tabbar-shown", !hidden);
    return () => document.documentElement.classList.remove("tk-tabbar-shown");
  }, [hidden]);
  const cover = hidden ? {} : { "data-covers-bottom": "" };

  if (partner) {
    const onDashboard = pathname === "/partner/dashboard";
    const current = onDashboard ? (new URLSearchParams(search).get("tab") || "overview") : null;
    return (
      <nav className="tk-tabbar" data-hidden={hidden} aria-label={l.label} data-testid="app-tab-bar" {...cover}>
        {PARTNER.map(({ tab, icon, key, unread: counts }) => {
          const active = current === tab;
          return (
            // A Link, not a NavLink: the five share one path, the ?tab= tells them apart.
            <Link key={key} to={tab === "overview" ? "/partner/dashboard" : `/partner/dashboard?tab=${tab}`}
              className="tk-tab tk-press" aria-current={active ? "page" : undefined} data-testid={`app-tab-${key}`}>
              <TabContent active={active} icon={icon} label={l[key]} count={counts ? unread : 0} countLabel={l.unread(unread)} />
            </Link>
          );
        })}
      </nav>
    );
  }

  const travellerTab = TRAVELLER.find(({ to }) => to !== "/" && (pathname === to || pathname.startsWith(`${to}/`)))?.key || "home";
  return (
    <nav className="tk-tabbar" data-hidden={hidden} aria-label={l.label} data-testid="app-tab-bar" {...cover}>
      {TRAVELLER.map(({ to, icon, key, unread: counts }) => {
        const active = key === travellerTab;
        return (
          <Link key={to} to={to} className="tk-tab tk-press" aria-current={active ? "page" : undefined} data-testid={`app-tab-${key}`}>
            <TabContent active={active} icon={icon} label={l[key]} count={counts ? unread : 0} countLabel={l.unread(unread)} />
          </Link>
        );
      })}
    </nav>
  );
}
